/**
 * Fixed dark palette for the LandingV2 "choose your persona" hero.
 *
 * This hero is always dark, independent of the visitor's color-mode. It
 * therefore cannot read from the theme-flipping `Colors` in `constants/ui`
 * (whose values swap under dark mode). These are the hero's own, like the
 * mobile `brand-colors` palette — every literal the hero needs lives here so
 * no component inlines a hex.
 */
export const LandingHero = {
  // Page surface
  pageGradient:
    'radial-gradient(1200px 600px at 50% -10%, #111A33 0%, #060913 60%)',
  pageBottom: '#060913',

  // Backdrop grid + orbs
  gridLine: 'rgba(148, 163, 184, 0.06)',
  orbGreen: '#10B981',
  orbSky: '#0EA5E9',

  // Text
  textPrimary: '#F8FAFC',
  textBody: '#E2E8F0',
  textMuted: '#9FB0C8',
  textMutedSoft: '#A5B4CB',
  textFaint: '#8796AE',
  chipText: '#D7E0EC',

  // Glass nav
  navGlassBg: 'rgba(15, 23, 42, 0.55)',
  navGlassBorder: 'rgba(148, 163, 184, 0.14)',
  navGlassShadow: 'rgba(0, 0, 0, 0.7)',
  navInsetHighlight: 'rgba(255, 255, 255, 0.05)',
  navPillBg: 'rgba(255, 255, 255, 0.04)',
  navPillBorder: 'rgba(148, 163, 184, 0.1)',
  navLink: '#A5B4CB',
  navLinkHover: '#F1F5F9',
  navLinkHoverBg: 'rgba(255, 255, 255, 0.07)',

  // Logo mark
  logoGradient: 'linear-gradient(135deg, #38BDF8, #0284C7)',
  logoShadow: 'rgba(56, 189, 248, 0.7)',
  logoInsetHighlight: 'rgba(255, 255, 255, 0.4)',
  logoText: '#FFFFFF',

  // Ghost button (Log in)
  ghostBorder: 'rgba(148, 163, 184, 0.25)',
  ghostBorderHover: 'rgba(148, 163, 184, 0.55)',
  ghostBgHover: 'rgba(255, 255, 255, 0.05)',

  // Green CTA (Sign up / card arrow / pro)
  ctaGradient: 'linear-gradient(135deg, #6EE7B7, #10B981)',
  ctaText: '#04140D',
  ctaShadow: 'rgba(52, 211, 153, 0.7)',
  ctaShadowHover: 'rgba(52, 211, 153, 0.75)',
  ctaInsetHighlight: 'rgba(255, 255, 255, 0.45)',
  ctaSheen: 'rgba(255, 255, 255, 0.55)',

  // Status badge pill
  badgeBg: 'rgba(56, 189, 248, 0.08)',
  badgeBorder: 'rgba(56, 189, 248, 0.25)',
  badgeShadow: 'rgba(56, 189, 248, 0.45)',
  badgeText: '#BAE6FD',
  liveDot: '#34D399',

  // Gradient headline
  headlineGradient:
    'linear-gradient(100deg, #6EE7B7 0%, #38BDF8 45%, #A78BFA 80%, #6EE7B7 100%)',

  // Shared card surface
  cardShadow: 'rgba(0, 0, 0, 0.8)',
  cardInsetHighlight: 'rgba(255, 255, 255, 0.06)',
  cardInsetHighlightHover: 'rgba(255, 255, 255, 0.08)',

  // Shared chip
  chipBg: 'rgba(255, 255, 255, 0.04)',
  chipBorder: 'rgba(148, 163, 184, 0.16)',

  // Trust row
  trustText: '#8796AE',
  trustCheck: '#34D399',

  // Pro accent
  pro: {
    ring:
      'linear-gradient(140deg, #6EE7B7, rgba(16, 185, 129, 0) 55%, #10B981)',
    cardBg:
      'linear-gradient(160deg, rgba(16, 185, 129, 0.1) 0%, rgba(15, 23, 42, 0.72) 42%, rgba(10, 15, 30, 0.85) 100%)',
    cardBorder: 'rgba(52, 211, 153, 0.18)',
    cardHoverShadow: 'rgba(16, 185, 129, 0.45)',
    cardHoverRing: 'rgba(52, 211, 153, 0.15)',
    kicker: '#6EE7B7',
    arrowGradient: 'linear-gradient(135deg, #6EE7B7, #10B981)',
    arrowText: '#04140D',
    arrowShadow: 'rgba(16, 185, 129, 0.8)',
    divider: 'linear-gradient(90deg, rgba(52, 211, 153, 0.35), rgba(148, 163, 184, 0.08))',
    chipHoverText: '#ECFDF5',
    chipHoverBg: 'rgba(52, 211, 153, 0.16)',
    chipHoverBorder: 'rgba(52, 211, 153, 0.5)',
    chipHoverShadow: 'rgba(52, 211, 153, 0.8)',
  },

  // Creator accent
  creator: {
    ring:
      'linear-gradient(140deg, #7DD3FC, rgba(56, 189, 248, 0) 55%, #A78BFA)',
    cardBg:
      'linear-gradient(160deg, rgba(56, 189, 248, 0.11) 0%, rgba(15, 23, 42, 0.72) 42%, rgba(10, 15, 30, 0.85) 100%)',
    cardBorder: 'rgba(56, 189, 248, 0.2)',
    cardHoverShadow: 'rgba(56, 189, 248, 0.45)',
    cardHoverRing: 'rgba(56, 189, 248, 0.15)',
    kicker: '#7DD3FC',
    arrowGradient: 'linear-gradient(135deg, #7DD3FC, #0EA5E9)',
    arrowText: '#03131F',
    arrowShadow: 'rgba(14, 165, 233, 0.8)',
    divider: 'linear-gradient(90deg, rgba(56, 189, 248, 0.4), rgba(148, 163, 184, 0.08))',
    chipHoverText: '#F0F9FF',
    chipHoverBg: 'rgba(56, 189, 248, 0.16)',
    chipHoverBorder: 'rgba(56, 189, 248, 0.5)',
    chipHoverShadow: 'rgba(56, 189, 248, 0.8)',
  },

  // Neutral glass card (sections without a strong accent)
  neutral: {
    ring:
      'linear-gradient(140deg, rgba(148, 163, 184, 0.45), rgba(148, 163, 184, 0) 55%, rgba(148, 163, 184, 0.25))',
    cardBg:
      'linear-gradient(160deg, rgba(148, 163, 184, 0.06) 0%, rgba(15, 23, 42, 0.72) 45%, rgba(10, 15, 30, 0.85) 100%)',
    cardBorder: 'rgba(148, 163, 184, 0.16)',
    cardHoverShadow: 'rgba(56, 189, 248, 0.22)',
    cardHoverRing: 'rgba(148, 163, 184, 0.18)',
  },

  // Band surface (marketplace / final CTA panels)
  band: {
    bg:
      'linear-gradient(160deg, rgba(56, 189, 248, 0.06) 0%, rgba(15, 23, 42, 0.78) 45%, rgba(10, 15, 30, 0.88) 100%)',
    border: 'rgba(148, 163, 184, 0.14)',
    shadow: 'rgba(0, 0, 0, 0.6)',
  },

  // Sky CTA (creator / secondary gradient actions)
  skyCtaGradient: 'linear-gradient(135deg, #7DD3FC, #0EA5E9)',
  skyCtaText: '#03131F',
  skyCtaShadow: 'rgba(14, 165, 233, 0.7)',

  // Decorative collab avatar chips
  collabAvatar: {
    ink: '#1E293B',
    sky: '#0EA5E9',
    pink: '#EC4899',
  },

  // Success pill
  successPillBg: 'rgba(52, 211, 153, 0.16)',
  successPillText: '#6EE7B7',
} as const;

export const HERO_FONT_DISPLAY = "'Sora', system-ui, sans-serif";
export const HERO_FONT_BODY = "'Manrope', system-ui, sans-serif";
